import {
  ArrowRightIcon,
  BeakerIcon,
  ClockIcon,
  HeartIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

/**
 * Services Section (JS version)
 * - Mismo contenido, solo layout/UX
 * - Sin TypeScript para evitar errores en .js
 */

const serviceCategories = {
  diagnostico: {
    title: 'Diagnóstico Cardiovascular',
    description: 'Estudios especializados para una evaluación completa del corazón',
    icon: MagnifyingGlassIcon,
    services: [
      {
        name: 'Ecocardiograma en reposo',
        description:
          'Estudio de ultrasonido cardíaco realizado en reposo para evaluar estructura y función',
        duration: '30-45 min',
        preparation: 'No requiere preparación especial',
        image: '/ramon_eco_reposo.png'
      },
      {
        name: 'Prueba de esfuerzo con ecocardiograma',
        description:
          'Ecocardiograma realizado durante ejercicio controlado en banda caminadora',
        duration: '45-60 min',
        preparation: 'Ropa deportiva, no comer 3 horas antes',
        image: '/esfuerzo.png'
      },
      {
        name: 'Ecografía Doppler',
        description: 'Ultrasonido especializado para evaluar el flujo sanguíneo en vasos',
        duration: '30 min',
        preparation: 'No requiere preparación especial',
        image: '/doppler.png'
      },
      {
        name: 'Ecografía Doppler carotídea',
        description:
          'Estudio de las arterias carótidas para detectar obstrucciones o estenosis',
        duration: '30 min',
        preparation: 'No requiere preparación especial',
        image: '/carotidea.png'
      },
      {
        name: 'Ecografía Doppler arterial periférico',
        description:
          'Evaluación de arterias periféricas en extremidades para detectar obstrucciones',
        duration: '30 min',
        preparation: 'No requiere preparación especial',
        image: '/periferico.png'
      },
      {
        name: 'Ecografía Doppler venoso periférico',
        description:
          'Evaluación de venas periféricas en extremidades para detectar trombosis',
        duration: '30 min',
        preparation: 'No requiere preparación especial',
        image: '/venoso.png'
      },
      {
        name: 'Electrocardiograma (ECG)',
        description: 'Registro de la actividad eléctrica del corazón',
        duration: '10-15 min',
        preparation: 'Ropa cómoda, evitar cremas en el pecho',
        image: '/electro_rep.png'
      },
      {
        name: 'Holter 24 horas',
        description: 'Monitoreo continuo del ritmo cardíaco durante 24 horas',
        duration: '24 horas',
        preparation: 'Ducha antes de la colocación',
        image: '/24_hours.png'
      }
    ]
  },
  tratamiento: {
    title: 'Tratamientos Especializados',
    description:
      'Procedimientos avanzados para el tratamiento de enfermedades cardiovasculares',
    icon: HeartIcon,
    services: [
      {
        name: 'Cateterismo cardíaco',
        description:
          'Procedimiento diagnóstico y terapéutico para evaluar y tratar arterias coronarias',
        duration: '60-90 min',
        preparation: 'Ayuno de 8 horas, análisis previos',
        image: '/cateterismo_cardiaca.png'
      },
      {
        name: 'Angioplastia con stents coronarios',
        description:
          'Apertura de arterias obstruidas con colocación de stents para restaurar el flujo',
        duration: '90-120 min',
        preparation: 'Hospitalización, estudios preoperatorios completos',
        image: '/angio_stents.png'
      },
      {
        name: 'Arritmias cardíacas',
        description:
          'Diagnóstico y tratamiento especializado de trastornos del ritmo cardíaco',
        duration: '60 min',
        preparation: 'Estudios electrofisiológicos previos',
        image: '/arritmias.png'
      },
      {
        name: 'Colocación de marcapaso definitivo',
        description:
          'Implantación de marcapaso permanente para regular el ritmo cardíaco',
        duration: '60-90 min',
        preparation: 'Evaluación completa, hospitalización',
        image: '/marcapaso.png'
      },
      {
        name: 'Ablación Cardíaca',
        description: 'Tratamiento de arritmias mediante radiofrecuencia',
        duration: '120-180 min',
        preparation: 'Estudios electrofisiológicos previos',
        image: '/ablacion.png'
      }
    ]
  },
  prevencion: {
    title: 'Prevención',
    description:
      'Programas integrales para la prevención de enfermedades cardiovasculares',
    icon: BeakerIcon,
    services: [
  {
    name: 'Check up cardiovascular',
    description: '',
    duration: '',
    preparation: '',
    image: '/check_up.png'
  },
  {
    name: 'Control de paciente diabético',
    description: '',
    duration: '',
    preparation: '',
    image: '/control_hipertension_arterial.png'
  },
  {
    name: 'Control de paciente con hipertensión arterial',
    description: '',
    duration: '',
    preparation: '',
    image: '/diabetes.png'
  },
  {
    name: 'Control de paciente adulto mayor',
    description: '',
    duration: '',
    preparation: '',
    image: '/paciente_mayor.png'
  },
  {
    name: 'Control de dislipidemias (colesterol y triglicéridos)',
    description: '',
    duration: '',
    preparation: '',
    image: '/dislipidemia.png'
  },
  {
    name: 'Control de paciente post-infarto',
    description: '',
    duration: '',
    preparation: '',
    image: '/post_infarto.png'
  },
  {
    name: 'Control de paciente post-embolia',
    description: '',
    duration: '',
    preparation: '',
    image: '/post_embolia.png'
  }
]
  }
};

const CategoryTabs = ({ categories, active, onChange }) => {
  return (
    <div className="relative mb-8 sm:mb-12">
      <div
        className="flex gap-2 sm:gap-3 overflow-x-auto no-scrollbar px-1 pb-2 sm:justify-center"
        role="tablist"
        aria-label="Categorías de servicios"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitScrollbar: 'none'
        }}
      >
        {categories.map((category) => {
          const { icon: IconComponent, title } = serviceCategories[category];
          const isActive = active === category;
          return (
            <button
              key={category}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${category}`}
              onClick={() => onChange(category)}
              className={[
                'flex items-center whitespace-nowrap px-3 sm:px-5 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 text-xs sm:text-sm lg:text-base',
                isActive
                  ? 'bg-accent text-primary shadow-lg scale-105'
                  : 'bg-surface text-primary hover:bg-accent/10 hover:text-black shadow'
              ].join(' ')}
            >
              <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="hidden sm:inline">{title}</span>
              <span className="sm:hidden">{title.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ServiceCard = ({ service, index }) => (
  <article
    key={index}
    className="group bg-surface rounded-xl sm:rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden focus-within:ring-2 focus-within:ring-primary/50"
  >
    <div className="flex flex-col sm:flex-row">
      {/* Image */}
      <div className="sm:w-1/3">
        <img
          src={service.image}
          alt={service.name}
          className="w-full h-100 sm:h-full object-cover group-hover:scale-110 transition-transform duration-500"
          loading="lazy"
        />
      </div>

      {/* Content */}
      <div className="flex-1 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-3 sm:mb-4">
          <h4 className="text-lg sm:text-xl lg:text-2xl font-bold text-primary group-hover:text-black transition-colors duration-300 font-serif mb-2 sm:mb-0 sm:mr-4">
            {service.name}
          </h4>
          <div className="flex items-center text-primary bg-accent/10 px-2 sm:px-3 py-1 rounded-full self-start sm:ml-4 shrink-0">
            <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            <span className="text-xs sm:text-sm font-medium font-sans">{service.duration}</span>
          </div>
        </div>

        <p className="text-text-secondary mb-4 sm:mb-6 leading-relaxed font-sans text-sm sm:text-base">
          {service.description}
        </p>

        <a
          href="#contacto"
          className="inline-flex items-center text-black hover:text-primary font-semibold transition-colors duration-300 group font-sans focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 text-sm sm:text-base"
        >
          Agendar Estudio
          <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
        </a>
      </div>
    </div>
  </article>
);

const Services = () => {
  const [activeCategory, setActiveCategory] = useState('diagnostico');

  const categories = useMemo(() => Object.keys(serviceCategories), []);
  const currentCategory = serviceCategories[activeCategory];

  return (
    <section id="servicios" className="py-12 sm:py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <header className="text-center mb-12 sm:mb-14 lg:mb-16">
          <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-primary/10 text-primary text-xs sm:text-sm font-semibold rounded-full mb-6 sm:mb-8">
            <HeartIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
            Nuestros Servicios
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4 sm:mb-6 font-serif px-4 sm:px-0">
            Atención Integral del <span className="text-marino">Corazón</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed font-sans px-4 sm:px-0 text-justify">
            Ofrecemos una gama completa de servicios cardiológicos, desde diagnóstico
            preventivo hasta tratamientos especializados de vanguardia.
          </p>
        </header>

        {/* Category Tabs */}
        <CategoryTabs
          categories={categories}
          active={activeCategory}
          onChange={setActiveCategory}
        />

        {/* Active Category Content */}
        <div className="mb-8 sm:mb-12" role="region" aria-labelledby={`panel-${activeCategory}`}>
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h3
              id={`panel-${activeCategory}`}
              className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary mb-3 sm:mb-4 font-serif px-4 sm:px-0"
            >
              {currentCategory.title}
            </h3>
            <p className="text-sm sm:text-base lg:text-lg text-text-secondary max-w-2xl mx-auto font-sans px-4 sm:px-0">
              {currentCategory.description}
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid gap-4 sm:gap-6 lg:gap-8 lg:grid-cols-2">
            {currentCategory.services.map((service, index) => (
              <ServiceCard service={service} index={index} key={service.name} />
            ))}
          </div>
        </div>

        {/* Emergency CTA (placeholder original) */}
      </div>
    </section>
  );
};

export default Services;
